/**
 * askPrompt — a drop-in replacement for window.prompt().
 *
 * Chrome refuses `prompt()` in embedded/sandboxed contexts and throws
 * "prompt() is not supported", which crashed every feature that used it
 * (saved views, canvas labels/folders/tags, note + newsletter link URLs).
 * This renders a real on-brand modal instead and resolves like prompt does:
 *   const name = await askPrompt("Name this view:");   // string, or null on cancel
 *
 * Imperative + promise-based on purpose: call sites only need `await` added,
 * so there's no provider to wire up and the diff stays a one-liner each.
 */
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { T } from "./tokens";

function PromptDialog({ message, defaultValue, placeholder, okLabel, required, onDone }) {
  const [value, setValue] = useState(defaultValue ?? "");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
    const onKey = (e) => { if (e.key === "Escape") onDone(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDone]);

  const submit = (e) => {
    e?.preventDefault();
    // `required` blocks an empty (or whitespace-only) answer and hands the
    // caller a trimmed string, so call sites never have to re-check.
    if (required) {
      const trimmed = value.trim();
      if (!trimmed) {
        setError(typeof required === "string" ? required : "This field is required.");
        inputRef.current?.focus();
        return;
      }
      onDone(trimmed);
      return;
    }
    onDone(value);
  };

  return (
    <div
      onClick={() => onDone(null)}
      style={{
        position: "fixed", inset: 0, zIndex: 3000, background: "rgba(17,24,39,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: "100%", maxWidth: 420, background: "var(--em-bg-card)", borderRadius: 14,
          padding: 20, boxShadow: "0 24px 64px rgba(0,0,0,0.25)", color: T.fg1,
        }}
      >
        <div style={{ font: `600 14px ${T.font}`, color: T.fg1, marginBottom: 10 }}>
          {message}
          {required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
        </div>
        <input
          ref={inputRef}
          value={value}
          placeholder={placeholder || ""}
          aria-invalid={error ? "true" : undefined}
          onChange={(e) => { setValue(e.target.value); if (error) setError(""); }}
          style={{
            width: "100%", boxSizing: "border-box",
            border: `1px solid ${error ? "#ef4444" : T.border}`,
            borderRadius: 8, padding: "9px 11px", font: `400 13.5px ${T.font}`,
            outline: "none", background: "var(--em-bg-card)", color: T.fg1,
          }}
        />
        {error && (
          <div style={{ font: `500 12px ${T.font}`, color: "#ef4444", marginTop: 6 }}>{error}</div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button
            type="button"
            onClick={() => onDone(null)}
            style={{
              padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)", color: T.fg2, font: `600 13px ${T.font}`, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              padding: "8px 16px", borderRadius: 8, border: 0, background: T.gradient,
              color: "#fff", font: `600 13px ${T.font}`, cursor: "pointer",
            }}
          >
            {okLabel || "OK"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * @param {object} [opts]
 * @param {string} [opts.placeholder]
 * @param {string} [opts.okLabel]
 * @param {boolean|string} [opts.required] — refuse an empty answer; pass a
 *        string to use it as the validation message. Resolves trimmed.
 * @returns {Promise<string|null>} the entered text, or null if cancelled —
 * matching window.prompt's contract so callers keep their null checks.
 */
export default function askPrompt(message, defaultValue = "", opts = {}) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (result) => {
      // Unmount on a later tick — React 19 warns if you unmount while the
      // event that triggered it is still being handled.
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
      resolve(result);
    };
    root.render(
      <PromptDialog
        message={message}
        defaultValue={defaultValue}
        placeholder={opts.placeholder}
        okLabel={opts.okLabel}
        required={opts.required}
        onDone={done}
      />
    );
  });
}
